import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Truck, IndianRupee, CheckCircle2, TrendingUp, Clock, ArrowRight, ArrowUpRight, MapPin, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { dashboardStats, currentUser, myLoads, generateLoads, laneInsights } from '../mock/mockData';
import { useI18n } from '../lib/i18n';
import { getUser } from '../lib/api';

const formatINR = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

const StatCard = ({ icon: Icon, label, value, change, color }) => (
  <div className="bg-white rounded-2xl border border-gray-100 p-5 hover-lift">
    <div className="flex items-start justify-between mb-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5" strokeWidth={2} />
      </div>
      {change && (
        <div className="flex items-center gap-1 text-xs font-medium text-[#00A651]">
          <ArrowUpRight className="w-3 h-3" />{change}
        </div>
      )}
    </div>
    <p className="text-2xl font-extrabold text-[#0B2545] font-[Poppins]">{value}</p>
    <p className="text-sm text-gray-500 mt-0.5">{label}</p>
  </div>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const stored = getUser();
  const firstName = (stored?.name || currentUser.name).split(' ')[0];
  const recentLoads = React.useMemo(() => generateLoads(5), []);

  return (
    <div className="space-y-6">
      {/* Welcome */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl lg:text-3xl font-extrabold text-[#0B2545] font-[Poppins]">{t('namaste')}, {firstName} 👋</h1>
          <p className="text-sm text-gray-500 mt-1">{t('dashboardSubtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => navigate('/app/loads')} variant="outline" className="border-gray-200">{t('findLoads')}</Button>
          <Button onClick={() => navigate('/app/trucks')} className="bg-[#00A651] hover:bg-[#008a43] text-white">{t('postTruck')} <ArrowRight className="w-4 h-4 ml-2" /></Button>
        </div>
      </div>

      {/* Trial banner */}
      <div className="bg-gradient-to-r from-[#0B2545] to-[#13315C] rounded-2xl p-5 flex flex-wrap items-center justify-between gap-4 relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-40 h-40 bg-[#00A651]/20 rounded-full blur-2xl"></div>
        <div className="relative">
          <p className="text-white font-semibold">{t('trialBanner')} {currentUser.subscription.daysLeft} {t('daysRemaining')}</p>
          <p className="text-white/70 text-sm mt-0.5">{t('trialUpsell')}</p>
        </div>
        <Button onClick={() => navigate('/app/subscription')} className="bg-[#00A651] hover:bg-[#008a43] text-white relative">{t('upgradeToPro')}</Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Package} label={t('activeLoads')} value={dashboardStats.activeLoads} change="+12%" color="bg-[#00A651]/10 text-[#00A651]" />
        <StatCard icon={Truck} label={t('availableTrucks')} value={dashboardStats.availableTrucks} change="+8%" color="bg-[#0B2545]/10 text-[#0B2545]" />
        <StatCard icon={IndianRupee} label={t('monthlyRevenue')} value={formatINR(dashboardStats.monthlyRevenue)} change="+18%" color="bg-[#FFB800]/15 text-[#B37800]" />
        <StatCard icon={CheckCircle2} label={t('onTimeDelivery')} value={`${dashboardStats.onTimeDelivery}%`} change="+2%" color="bg-[#00A651]/10 text-[#00A651]" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Active Shipments */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-bold text-[#0B2545]">Active Shipments</h2>
              <p className="text-xs text-gray-500">Track your ongoing trips in real-time</p>
            </div>
            <Button variant="ghost" onClick={() => navigate('/app/my-loads')} className="text-[#00A651] hover:text-[#008a43] hover:bg-[#00A651]/5">View all</Button>
          </div>
          <div className="space-y-3">
            {myLoads.slice(0, 3).map(l => (
              <div key={l.id} className="border border-gray-100 rounded-xl p-4">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-gray-500">{l.id}</span>
                    <Badge className={`border-0 text-xs ${l.status === 'In Transit' ? 'bg-[#00A651]/10 text-[#00A651]' : l.status === 'Delivered' ? 'bg-blue-50 text-blue-600' : l.status === 'Loading' ? 'bg-[#FFB800]/15 text-[#B37800]' : 'bg-gray-100 text-gray-600'}`}>{l.status}</Badge>
                  </div>
                  <span className="font-bold text-[#00A651]">{formatINR(l.rate)}</span>
                </div>
                <div className="flex items-center gap-2 text-sm font-semibold text-[#0B2545] mb-2">
                  <span>{l.origin.split(',')[0]}</span>
                  <ArrowRight className="w-4 h-4 text-gray-400" />
                  <span>{l.destination.split(',')[0]}</span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-2">
                  <div className="h-full brand-gradient" style={{ width: `${l.progress}%` }}></div>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
                  <span>{l.material} • {l.weight} • {l.vehicleNumber}</span>
                  <span>{l.progress}% complete</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick loads */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-[#0B2545]">Live Loads</h2>
            <Badge className="bg-red-50 text-red-600 border-0"><span className="w-1.5 h-1.5 bg-red-500 rounded-full mr-1 animate-pulse"></span>LIVE</Badge>
          </div>
          <div className="space-y-2">
            {recentLoads.slice(0, 4).map(l => (
              <div key={l.id} onClick={() => navigate('/app/loads')} className="border border-gray-100 rounded-lg p-3 hover:border-[#00A651]/40 cursor-pointer transition-colors">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-1.5 text-sm font-semibold text-[#0B2545]">
                    <MapPin className="w-3 h-3 text-[#00A651]" />
                    <span>{l.origin.split(',')[0]}</span>
                    <ArrowRight className="w-3 h-3 text-gray-400" />
                    <span>{l.destination.split(',')[0]}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">{l.weight} • {l.truckCode}</span>
                  <span className="text-sm font-bold text-[#00A651]">{formatINR(l.rate)}</span>
                </div>
              </div>
            ))}
          </div>
          <Button onClick={() => navigate('/app/loads')} variant="outline" className="w-full mt-3 border-gray-200">See All Loads</Button>
        </div>
      </div>

      {/* Top Lanes */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold text-[#0B2545]">Top Lane Rates</h2>
            <p className="text-xs text-gray-500">Real-time freight rate insights across India</p>
          </div>
          <Button onClick={() => navigate('/app/insights')} variant="ghost" className="text-[#00A651]">View insights <ArrowRight className="w-4 h-4 ml-1" /></Button>
        </div>
        <div className="overflow-x-auto -mx-5 px-5">
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-500 uppercase">
              <tr className="border-b border-gray-100">
                <th className="text-left py-2 font-medium">Lane</th>
                <th className="text-right py-2 font-medium">Avg Rate</th>
                <th className="text-right py-2 font-medium hidden md:table-cell">₹/km</th>
                <th className="text-right py-2 font-medium hidden md:table-cell">Distance</th>
                <th className="text-right py-2 font-medium">Trend</th>
              </tr>
            </thead>
            <tbody>
              {laneInsights.slice(0, 5).map((l, i) => (
                <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="py-3 font-semibold text-[#0B2545]">{l.lane}</td>
                  <td className="py-3 text-right font-medium">{formatINR(l.avgRate)}</td>
                  <td className="py-3 text-right hidden md:table-cell">₹{l.ratePerKm}</td>
                  <td className="py-3 text-right text-gray-500 hidden md:table-cell">{l.distance} km</td>
                  <td className={`py-3 text-right font-medium ${l.trend === 'up' ? 'text-[#00A651]' : l.trend === 'down' ? 'text-red-500' : 'text-gray-500'}`}>
                    {l.change > 0 ? '+' : ''}{l.change}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
